import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { createStyles, Menu } from "@mantine/core"
import { Icon } from "@/components/Icon"
import MantineHideColumnTrigger from "./MantineHideColumnTrigger"
import { isColumnActivelyFiltered } from "app/static/frontend/sheets/mantine-sheet/mantine-sheet-helper-functions"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

export const useStyles = createStyles((theme) => ({
    groupColumn: {
        "& svg": {
            marginRight: "0.1rem",
        },
    },
    hideColumnToggle: {
        "& i": {
            marginRight: "0.25rem",
        },
        "& i.fa.fa-toggle-on": {
            color: `${theme.colors.violet[5]}`,
        },
    },
    item: {
        "& *": {
            fontSize: "14px",
        },
        "& svg": {
            marginRight: "0.5rem",
            color: theme.colors.violet[8],
        },
        "&:hover, &:focus": {
            textDecoration: "none",
            backgroundColor: theme.colors.violet[1],
            color: theme.colors.violet[8],
        },
        "&": {
            color: theme.colors.gray[7],
        },
    },
    linkButton: {
        "& a": {
            color: "inherit",
        },
    },
    removeColumn: {
        "&": {
            color: "#C32219",
        },
        "& svg": {
            marginRight: "0.15rem",
            color: "#C32219",
        },
        "&:hover, &:focus": {
            color: "#C32219",
        },
    },
    renameColumn: {
        "& svg": {
            marginRight: "0.1rem",
        },
    },
    unpinSort: {
        "&": {
            color: "#C32219",
        },
        "& svg": {
            color: "#C32219",
        },
        "&:hover, &:focus": {
            color: "#C32219",
        },
    },
    pinnedIcon: {
        backgroundColor: theme.colors.violet[2],
        borderRadius: "50%",
        margin: "0px 5px",
        padding: "0px 5px",
        cursor: "default",

        "&:hover, &:focus, &:active": {
            backgroundColor: theme.colors.violet[2],
        },
    },
}))

export const ColumnActionMenu = ({
    canAddColumns,
    canHideColumns,
    canRemoveColumns,
    isDatumColumn,
    sheetId,
    columnIndex,
    onRemoveColumn,
    columnId,
    toggleColumnIsHiddenOnPublic,
    isHidden,
    canRenameColumns,
    onRenameColumn,
    canGroupColumns,
    columnDefinition,
    canColorColumns,
    table,
    isPinned,
    onPinSort,
}) => {
    const { classes, cx } = useStyles()
    const useSmallColumnMode = columnDefinition.getSize() < 144
    const isFiltered = isColumnActivelyFiltered(columnDefinition)
    const sortedState = columnDefinition.getIsSorted()

    useEffect(() => {
        // We had to make this effect to input the tabIndex for every button after they were created.
        // Menu.Item creates the buttons with a -1 value, leading to the buttons not being available for tabbing.
        const menuItems = document.querySelectorAll('.mantineColumnActionMenu button[role="menuitem"]')
        menuItems.forEach((item) => {
            item.tabIndex = 0
        })
    }, [])

    const sections = [
        [
            canGroupColumns && (
                <Menu.Item
                    className={cx(classes.item, classes.groupColumn)}
                    onClick={() => columnDefinition.toggleGrouping()}
                    icon={<Icon icon="layer-group" />}
                    data-plum-id="group-by-column"
                >
                    {columnDefinition.getIsGrouped() ? "Ungroup column" : `Group by this column`}
                </Menu.Item>
            ),
            canAddColumns && !columnDefinition.getIsGrouped() && (
                <>
                    {!isDatumColumn && (
                        <Menu.Item
                            className={cx(classes.item, classes.linkButton)}
                            data-plum-id="add-column-right-left"
                        >
                            <a href={`/sheet/${sheetId}/add_column/${columnIndex}/`} tabIndex={-1}>
                                <Icon icon="arrow-from-right" /> Add column to the left
                            </a>
                        </Menu.Item>
                    )}
                    <Menu.Item className={cx(classes.item, classes.linkButton)} data-plum-id="add-column-right-left">
                        <a href={`/sheet/${sheetId}/add_column/${columnIndex + 1}/`} tabIndex={-1}>
                            <Icon icon="arrow-from-left" /> Add column to the right
                        </a>
                    </Menu.Item>
                </>
            ),
        ],
        columnDefinition.getCanFilter() && [
            isFiltered ? (
                <Menu.Item
                    className={cx(classes.item, classes.groupColumn)}
                    onClick={() => columnDefinition.setFilterValue()}
                    icon={<Icon icon="filter-slash" iconFamily="q" />}
                    data-plum-id="column-action-menu-filter"
                >
                    Clear filter
                </Menu.Item>
            ) : (
                <Menu.Item
                    className={cx(classes.item, classes.groupColumn)}
                    onClick={() => table.setShowColumnFilters((current) => !current)}
                    icon={<Icon icon="filter" />}
                    data-plum-id="column-action-menu-filter"
                >
                    Filter
                </Menu.Item>
            ),
        ],
        isFeatureEnabled("ff_pin_sort_sheets") && [
            <Menu.Item
                className={cx(classes.item, classes.groupColumn, isPinned ? classes.unpinSort : null)}
                onClick={() => onPinSort(columnId)}
                icon={<Icon icon="thumbtack" />}
                data-plum-id="column-action-menu-pin-sort"
            >
                {isPinned ? "Unpin sort" : "Pin sort"}
            </Menu.Item>,
        ],
        useSmallColumnMode &&
            columnDefinition.getCanSort() && [
                sortedState && (
                    <Menu.Item
                        className={cx(classes.item)}
                        onClick={() => columnDefinition.clearSorting()}
                        icon={<Icon icon="bars-staggered" iconFamily="q" />}
                        data-plum-id="column-sort"
                    >
                        Clear sorting
                    </Menu.Item>
                ),
                // Descending -> Ascending sort
                sortedState !== "asc" && (
                    <Menu.Item
                        className={cx(classes.item)}
                        onClick={() => columnDefinition.toggleSorting(false)}
                        icon={<Icon icon="sort-amount-up" iconFamily="fal" />}
                        data-plum-id="column-sort"
                    >
                        Sort ascending
                    </Menu.Item>
                ),
                // Ascending -> Descending sort
                sortedState !== "desc" && (
                    <Menu.Item
                        className={cx(classes.item)}
                        onClick={() => columnDefinition.toggleSorting(true)}
                        icon={<Icon icon="sort-amount-down" iconFamily="fal" />}
                        data-plum-id="column-sort"
                    >
                        Sort descending
                    </Menu.Item>
                ),
            ],
        [
            canRenameColumns && (
                <Menu.Item
                    className={cx(classes.item, classes.renameColumn)}
                    onClick={onRenameColumn}
                    icon={<Icon icon="tag" />}
                    data-plum-id="column-action-menu-rename"
                >
                    Rename column
                </Menu.Item>
            ),
            canColorColumns && (
                <Menu.Item className={cx(classes.item, classes.linkButton)}>
                    <a href={`/sheet/${sheetId}/setup_custom_column_colors/${columnIndex}`} tabIndex={-1}>
                        <Icon icon="paint-brush-alt" /> Custom color
                    </a>
                </Menu.Item>
            ),
            canHideColumns && (
                <Menu.Item
                    className={cx(classes.item, classes.hideColumnToggle)}
                    icon={
                        <MantineHideColumnTrigger
                            columnId={columnId}
                            handleHideColumn={toggleColumnIsHiddenOnPublic}
                            isHidden={isHidden}
                            sheetKey={sheetId}
                        />
                    }
                    onClick={toggleColumnIsHiddenOnPublic}
                >
                    {isHidden ? "Hidden from external sheet" : "Visible in external sheet"}
                </Menu.Item>
            ),
        ],
        [
            canRemoveColumns && (
                <Menu.Item
                    className={cx(classes.item, classes.removeColumn)}
                    onClick={() => {
                        if (columnDefinition.getIsGrouped()) {
                            columnDefinition.toggleGrouping()
                        }
                        onRemoveColumn()
                    }}
                    icon={<Icon icon="trash" />}
                    data-plum-id="column-action-menu-remove"
                >
                    Remove column
                </Menu.Item>
            ),
        ],
    ]
    // Create dividers after every section only if there is an actual element to be rendered in the section
    // and the section is an array
    // Ex. sections = [[false, Item], [Item, Item], [false],  false, [Item]]
    // becomes [[Item]], [Item, Item], [], [Item]], which we then filter out any
    // evaluated empty sections [[Item], [Item, Item], [Item]]
    // and add dividers to every section except the last [[Item, Divider], [Item, Item, Divider], [Item]]
    const sectionsToRender = sections
        .reduce(
            (sections, currSection) => (currSection ? sections.concat([currSection.filter((item) => item)]) : sections),
            [],
        )
        .filter((section) => section.length > 0)
    return (
        <div className="mantineColumnActionMenu">
            {sectionsToRender.map((section, index) =>
                index !== sectionsToRender.length - 1 ? section.concat(<Menu.Divider />) : section,
            )}
        </div>
    )
}

ColumnActionMenu.propTypes = {
    canAddColumns: PropTypes.bool,
    canCustomColorColumn: PropTypes.bool,
    canHideColumn: PropTypes.bool,
    canRemoveColumn: PropTypes.bool,
    isDatumColumn: PropTypes.bool,
    sheetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    columnIndex: PropTypes.number,
    onRemoveColumn: PropTypes.func,
    columnId: PropTypes.number,
    toggleColumnIsHiddenOnPublic: PropTypes.func,
    isHidden: PropTypes.bool,
    isPinned: PropTypes.bool,
    onPinSort: PropTypes.func,
}

export default ColumnActionMenu
